/**
 * Klient API. Wszystkie błędy przychodzą w jednym kształcie { code, message, details? },
 * gdzie `message` jest po polsku i nadaje się do pokazania wprost użytkownikowi —
 * dlatego interfejs nie tłumaczy kodów błędów na własne komunikaty.
 */
export type Rola = 'KLIENT' | 'SERWISANT' | 'BIURO' | 'ADMIN';

export interface Uzytkownik {
  id: string;
  email: string;
  imieNazwisko: string;
  rola: Rola;
  rolaNazwa: string;
  tenant: { id: string; nazwa: string; slug: string };
}

export interface Sesja {
  user: Uzytkownik;
  permissions: string[];
}

export interface ZdarzenieAudytu {
  id: string;
  akcja: string;
  encja: string;
  encjaId: string | null;
  at: string;
  user: { id: string; imieNazwisko: string } | null;
}

// --- Etap 2: karta urządzenia -------------------------------------------------

export type StatusUrzadzenia = 'SPRAWNE' | 'W_NAPRAWIE' | 'WYCOFANE' | 'ZASTEPCZE_U_KLIENTA';
export type Pochodzenie = 'NOWA' | 'UZYWANA';
export type ZrodloKarty = 'MONTAZ' | 'SERWIS' | 'IMPORT_SUBIEKT' | 'RECZNIE';
export type TypAtrybutu = 'TEKST' | 'LICZBA' | 'BOOL' | 'DATA' | 'WYBOR';

export interface Kontrahent {
  id: string;
  nazwa: string;
  nip: string | null;
  adres: string | null;
  telefon: string | null;
  email: string | null;
  osobaKontaktowa: string | null;
  subiektId: number | null;
  syncedAt: string | null;
  urzadzen: number;
}

export interface Kategoria {
  id: string;
  kod: string;
  nazwa: string;
  parentId: string | null;
  kolejnosc: number;
  aktywna: boolean;
  polaWymagane: string[];
  dzieci: Kategoria[];
}

export interface DefinicjaAtrybutu {
  id: string;
  categoryId: string;
  kod: string;
  nazwa: string;
  typ: TypAtrybutu;
  jednostka: string | null;
  wymagany: boolean;
  walidacja: { min?: number; max?: number; opcje?: string[] } | null;
  kolejnosc: number;
}

/** Wiersz listy urządzeń — tylko to, co widać w tabeli, plus liczba braków. */
export interface UrzadzenieWiersz {
  id: string;
  sideNumber: string;
  serialNumber: string;
  qrCode: string;
  producent: string;
  model: string;
  status: StatusUrzadzenia;
  customer: { id: string; nazwa: string };
  category: { id: string; kod: string; nazwa: string };
  nastepnyPrzeglad: string | null;
  braki: number;
}

export interface Urzadzenie extends Omit<UrzadzenieWiersz, 'braki'> {
  customerId: string;
  categoryId: string;
  sealDate: string | null;
  origin: Pochodzenie | null;
  source: ZrodloKarty;
  modelGlowicy: string | null;
  modelNapedu: string | null;
  podstawa: string | null;
  blat: string | null;
  inne: string[];
  rokProdukcji: number | null;
  dataZakupu: string | null;
  dataInstalacji: string | null;
  subiektDokumentId: number | null;
  gwarancjaDo: string | null;
  sprezonePowietrze: boolean | null;
  licznikMotogodzin: number | null;
  atrybuty: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface NowyKontrahent {
  clientOperationId?: string;
  nazwa: string;
  nip?: string;
  adres?: string;
  telefon?: string;
  osobaKontaktowa?: string;
}

export interface ListaUrzadzen {
  urzadzenia: UrzadzenieWiersz[];
  ile: number;
  /** Liczniki do chipów filtrów — liczone bez filtra statusu, żeby nie zerowały się nawzajem. */
  podsumowanie: { wszystkie: number; wgStatusu: Partial<Record<StatusUrzadzenia, number>> };
}

export interface Brak {
  kod: string;
  etykieta: string;
  rodzaj: 'pole' | 'atrybut';
}

export interface Karta {
  urzadzenie: Urzadzenie;
  kategoria: Omit<Kategoria, 'dzieci'>;
  sciezkaKategorii: { id: string; kod: string; nazwa: string }[];
  atrybuty: DefinicjaAtrybutu[];
  braki: Brak[];
}

export interface NowaKarta {
  /** Idempotencja powtórzonej wysyłki (docs/adr/0003) — patrz src/operacje.ts. */
  clientOperationId?: string;
  customerId: string;
  categoryId: string;
  serialNumber: string;
  producent: string;
  model: string;
  sideNumber?: string;
  origin?: Pochodzenie;
  atrybuty?: Record<string, unknown>;
}

/** Zmiana karty — dowolny podzbiór pól. Atrybuty dopisują się, `null` kasuje jeden. */
export type ZmianaKarty = Partial<Omit<NowaKarta, 'atrybuty'>> & {
  sealDate?: string | null;
  rokProdukcji?: number | null;
  dataZakupu?: string | null;
  dataInstalacji?: string | null;
  gwarancjaDo?: string | null;
  nastepnyPrzeglad?: string | null;
  sprezonePowietrze?: boolean | null;
  licznikMotogodzin?: number | null;
  modelGlowicy?: string | null;
  modelNapedu?: string | null;
  podstawa?: string | null;
  blat?: string | null;
  atrybuty?: Record<string, unknown>;
};


// --- Etap 3: montaż -----------------------------------------------------------

export type StatusMontazu = 'NOWE' | 'W_TOKU' | 'WYKONANE';
export type RodzajMontazu = 'NOWA' | 'UZYWANA';
export type RodzajPodpisu = 'wydal' | 'wykonal' | 'sprawdzil';

export interface Osoba {
  id: string;
  imieNazwisko: string;
}

export interface PozycjaCzesci {
  id: string;
  numerCzesci: string | null;
  opis: string;
  /** Prisma oddaje `numeric` jako tekst — nie zamieniamy go na float (CLAUDE.md §3). */
  ilosc: string | number;
  subiektTowarId: number | null;
  kolejnosc: number;
}

export interface MontazWiersz {
  id: string;
  numer: string;
  numerZamowienia: string | null;
  rodzaj: RodzajMontazu;
  status: StatusMontazu;
  dataZlecenia: string;
  dataRealizacji: string | null;
  producent: string | null;
  modelGlowicy: string | null;
  customer: { id: string; nazwa: string };
  podpisow: number;
}

export interface Montaz extends MontazWiersz {
  customerId: string;
  zlecajacyId: string;
  modelNapedu: string | null;
  podstawa: string | null;
  blat: string | null;
  inne: string[];
  serialNumber: string | null;
  numerDokumentu: string | null;
  typDokumentu: string | null;
  uwagi: string | null;
  uwagiWykonania: string | null;
  wydalAt: string | null;
  wykonalAt: string | null;
  sprawdzilAt: string | null;
  zlecajacy: Osoba;
  wydalBy: Osoba | null;
  wykonalBy: Osoba | null;
  sprawdzilBy: Osoba | null;
  czesci: PozycjaCzesci[];
  devices: { id: string; sideNumber: string; qrCode: string; producent: string; model: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface KartaMontazu {
  montaz: Montaz;
  podpisy: { zlozone: RodzajPodpisu[]; brakujace: RodzajPodpisu[]; komplet: boolean };
  /** Czy wolno teraz założyć kartę urządzenia. */
  mozeZakonczyc: boolean;
}

export interface ListaMontazy {
  montaze: MontazWiersz[];
  ile: number;
  podsumowanie: { wszystkie: number; wgStatusu: Partial<Record<StatusMontazu, number>> };
}

export interface NowaPozycjaCzesci {
  numerCzesci?: string;
  opis: string;
  ilosc?: number;
}

export interface NowyMontaz {
  clientOperationId?: string;
  customerId: string;
  rodzaj: RodzajMontazu;
  dataZlecenia?: string;
  numerZamowienia?: string;
  dataRealizacji?: string;
  producent?: string;
  modelGlowicy?: string;
  modelNapedu?: string;
  podstawa?: string;
  blat?: string;
  inne?: string[];
  serialNumber?: string;
  numerDokumentu?: string;
  typDokumentu?: string;
  uwagi?: string;
  uwagiWykonania?: string;
  czesci?: NowaPozycjaCzesci[];
}

export type ZmianaMontazu = Partial<NowyMontaz>;

/** Dane karty urządzenia, których nie ma na druku montażu — pytamy o nie na końcu. */
export interface ZakonczenieMontazu {
  clientOperationId?: string;
  categoryId: string;
  producent: string;
  model: string;
  serialNumber: string;
  sideNumber?: string;
  rokProdukcji?: number;
}


// --- Etap 4: serwis ------------------------------------------------------------

export type StatusZlecenia =
  | 'NOWE'
  | 'W_TOKU'
  | 'WSTRZYMANE'
  | 'ZAKONCZONE'
  | 'ROZLICZONE'
  | 'ODRZUCONE';
export type TrybZlecenia = 'WYJAZD' | 'WARSZTAT';
export type KanalZgloszenia = 'QR' | 'PORTAL' | 'KONSULTANT' | 'SERWISANT';
export type RodzajNaprawy =
  | 'GWARANCYJNA'
  | 'POGWARANCYJNA'
  | 'NIEOBJETA_GWARANCJA'
  | 'REGULACJA';

export interface PozycjaSlownika {
  kod: string;
  nazwa: string;
}

export interface SlownikiSerwisu {
  objawy: PozycjaSlownika[];
  czynnosci: PozycjaSlownika[];
}

/** Przycisk z maszyny stanów. Interfejs go rysuje, ale o niczym nie decyduje. */
export interface CzynnoscZlecenia {
  akcja: string;
  nazwa: string;
  wymagaPowodu: boolean;
  /** Czy prowadzi zlecenie do przodu — tylko taka czynność bywa akcją główną. */
  postep: boolean;
  dostepna: boolean;
  /** Czemu nieaktywna — np. „Zlecenie zamyka podpisany protokół serwisowy". */
  przeszkoda: string | null;
}

export interface ZlecenieWiersz {
  id: string;
  numer: string;
  status: StatusZlecenia;
  tryb: TrybZlecenia;
  kanal: KanalZgloszenia;
  priorytet: string;
  opisUsterki: string;
  dataZgloszenia: string;
  termin: string | null;
  wstrzymaniePowod: string | null;
  customer: { id: string; nazwa: string };
  device: { id: string; sideNumber: string; producent: string; model: string };
  technician: Osoba | null;
}

export interface WizytaZlecenia {
  id: string;
  termin: string;
  rozpoczetaAt: string | null;
  zakonczonaAt: string | null;
  uwagi: string | null;
  technician: Osoba;
  protokol: { id: string } | null;
}

export interface PodpisProtokolu {
  image: string;
  kto: string;
  kiedy: string;
  urzadzenie: string;
  /** Skrót treści w chwili podpisania — liczony przez serwer. */
  hashTresci: string;
}

export interface ProtokolZlecenia {
  id: string;
  rodzajNaprawy: RodzajNaprawy;
  czynnosci: string[];
  opis: string | null;
  czasNaprawyH: string | number;
  zalecenia: string | null;
  wykonaneAt: string;
  podpisKlienta: PodpisProtokolu;
  technician: Osoba;
  czesci: {
    id: string;
    numerCzesci: string | null;
    opis: string;
    ilosc: string | number;
    cena: string | number;
  }[];
}

export interface Zlecenie extends ZlecenieWiersz {
  customerId: string;
  deviceId: string;
  technicianId: string | null;
  objawy: string[];
  wyposazenie: string | null;
  polka: string | null;
  zglaszajacy: Osoba | null;
  wizyty: WizytaZlecenia[];
  protokoly: ProtokolZlecenia[];
  createdAt: string;
  updatedAt: string;
}

export interface KartaZlecenia {
  zlecenie: Zlecenie;
  czynnosci: CzynnoscZlecenia[];
  /** Stany, których nie ma w bazie — liczone z danych (docs/model-danych.md §5). */
  fakty: {
    zaplanowane: boolean;
    bezPrzypisania: boolean;
    wymagaKolejnejWizyty: boolean;
    doRozliczenia: boolean;
  };
}

export interface ListaZlecen {
  zlecenia: ZlecenieWiersz[];
  ile: number;
  podsumowanie: {
    wszystkie: number;
    wgStatusu: Partial<Record<StatusZlecenia, number>>;
    bezPrzypisania: number;
  };
}

export interface NoweZlecenie {
  clientOperationId?: string;
  customerId: string;
  deviceId: string;
  opisUsterki: string;
  objawy?: string[];
  tryb?: TrybZlecenia;
  kanal?: KanalZgloszenia;
  wyposazenie?: string;
  polka?: string;
  priorytet?: string;
}

export interface NowyProtokol {
  clientOperationId?: string;
  rodzajNaprawy: RodzajNaprawy;
  czynnosci: string[];
  opis?: string;
  czasNaprawyH?: number;
  zalecenia?: string;
  visitId?: string;
  podpisKlienta: { image: string; kto: string };
  czesci?: { numerCzesci?: string; opis: string; ilosc?: number; cena?: number }[];
}

export interface Zdrowie {
  status: string;
  aplikacja: string;
  wersja: string;
  zaktualizowano: string;
  baza: string;
  /** Nazwa środowiska z API (`lokalne`, `testowe`, `produkcja`). */
  srodowisko?: string;
}

export class BladApi extends Error {
  readonly code: string;
  readonly details?: unknown;

  constructor(code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'BladApi';
    this.code = code;
    this.details = details;
  }
}

/**
 * Komunikat, gdy pod adresem API nie stoi API. W developmencie to prawie zawsze
 * jedna przyczyna — uruchomiony sam frontend — więc mówimy o niej wprost;
 * serwisantowi w hali nazwa komendy npm nie pomoże w niczym.
 */
const KOMUNIKAT_BRAK_API = import.meta.env.DEV
  ? 'Serwer API nie odpowiada. Uruchom go w drugim terminalu: npm run dev:api'
  : 'Serwer nie odpowiada. Spróbuj ponownie za chwilę.';

function czyJson(odpowiedz: Response): boolean {
  return (odpowiedz.headers.get('Content-Type') ?? '').includes('application/json');
}

const KLUCZ_TOKENU = 'klindro.token';

export const token = {
  odczytaj: (): string | null => localStorage.getItem(KLUCZ_TOKENU),
  zapisz: (wartosc: string): void => localStorage.setItem(KLUCZ_TOKENU, wartosc),
  usun: (): void => localStorage.removeItem(KLUCZ_TOKENU),
};

async function zapytaj<T>(sciezka: string, opcje: RequestInit = {}): Promise<T> {
  const naglowki = new Headers(opcje.headers);
  naglowki.set('Accept', 'application/json');
  if (opcje.body) naglowki.set('Content-Type', 'application/json');

  const zapisany = token.odczytaj();
  if (zapisany) naglowki.set('Authorization', `Bearer ${zapisany}`);

  let odpowiedz: Response;
  try {
    odpowiedz = await fetch(`/api${sciezka}`, { ...opcje, headers: naglowki });
  } catch {
    throw new BladApi('NETWORK', 'Brak połączenia z serwerem.');
  }

  // Odpowiedź, która nie jest JSON-em, nie pochodzi od naszego API — to proxy
  // deweloperskie albo serwer WWW odpowiadający za nieżyjący backend. Bez tego
  // rozróżnienia „API nie chodzi" wygląda jak „wystąpił nieoczekiwany błąd",
  // czyli jak zepsuta aplikacja.
  if (!czyJson(odpowiedz)) {
    throw new BladApi('API_NIEDOSTEPNE', KOMUNIKAT_BRAK_API);
  }

  const tresc: unknown = await odpowiedz.json().catch(() => null);

  if (!odpowiedz.ok) {
    const blad = tresc as { code?: string; message?: string; details?: unknown } | null;
    throw new BladApi(
      blad?.code ?? 'INTERNAL',
      blad?.message ?? 'Wystąpił nieoczekiwany błąd.',
      blad?.details,
    );
  }

  return tresc as T;
}

/** Do czego doczepiony jest załącznik (docs/model-danych.md §3.7). */
export type RodzajWlasciciela = 'DEVICE' | 'TICKET' | 'REPORT' | 'ASSEMBLY' | 'MODEL';

/** Po co jest plik — nie „jakiego jest typu", to mówi MIME. */
export type RodzajZalacznika =
  | 'ZDJECIE_OGOLNE'
  | 'TABLICZKA'
  | 'PO_NAPRAWIE'
  | 'DTR'
  | 'PARTS_BOOK';

export interface Zalacznik {
  id: string;
  ownerType: RodzajWlasciciela;
  ownerId: string;
  kind: RodzajZalacznika;
  mime: string;
  bytes: number;
  szerokosc: number | null;
  wysokosc: number | null;
  opis: string | null;
  createdAt: string;
  createdBy?: { id: string; imieNazwisko: string } | null;
}

export interface NowyZalacznik {
  clientOperationId?: string;
  ownerType: RodzajWlasciciela;
  ownerId: string;
  kind: RodzajZalacznika;
  mime: string;
  /** Sam base64, bez przedrostka `data:`. */
  dane: string;
  szerokosc?: number;
  wysokosc?: number;
  opis?: string;
}

/** Ustawienia firmy — patrz ekran Ustawienia. */
export interface Ustawienia {
  zdjeciaWymaganePrzyKarcie: boolean;
}

export const api = {
  zdrowie: () => zapytaj<Zdrowie>('/health'),

  zalaczniki: (ownerType: RodzajWlasciciela, ownerId: string) =>
    zapytaj<{ zalaczniki: Zalacznik[] }>(
      `/attachments?ownerType=${ownerType}&ownerId=${encodeURIComponent(ownerId)}`,
    ),

  dodajZalacznik: (dane: NowyZalacznik) =>
    zapytaj<Zalacznik>('/attachments', { method: 'POST', body: JSON.stringify(dane) }),

  /**
   * Zawartość pliku jako adres obiektowy do `<img src>`.
   *
   * Osobno od `zapytaj`, bo to nie JSON — a `<img>` nie umie wysłać nagłówka
   * z tokenem, więc plik trzeba pobrać ręcznie i zamienić na blob.
   * Adres zwalnia wywołujący (`URL.revokeObjectURL`).
   */
  plikZalacznika: async (id: string): Promise<string> => {
    const naglowki = new Headers({ Accept: '*/*' });
    const zapisany = token.odczytaj();
    if (zapisany) naglowki.set('Authorization', `Bearer ${zapisany}`);

    const odpowiedz = await fetch(`/api/attachments/${id}/plik`, { headers: naglowki });
    if (!odpowiedz.ok) throw new BladApi('ZALACZNIK_NIEDOSTEPNY', 'Nie udało się pobrać pliku.');

    return URL.createObjectURL(await odpowiedz.blob());
  },

  usunZalacznik: (id: string) =>
    zapytaj<{ zalaczniki: Zalacznik[] }>(`/attachments/${id}`, { method: 'DELETE' }),

  ustawienia: () => zapytaj<Ustawienia>('/settings'),

  zapiszUstawienia: (zmiana: Partial<Ustawienia>) =>
    zapytaj<Ustawienia>('/settings', { method: 'PUT', body: JSON.stringify(zmiana) }),

  zaloguj: (email: string, password: string) =>
    zapytaj<Sesja & { token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  ja: () => zapytaj<Uzytkownik & { permissions: string[] }>('/auth/me'),

  dziennik: (limit = 20) => zapytaj<{ zdarzenia: ZdarzenieAudytu[] }>(`/audit?limit=${limit}`),

  kategorie: () => zapytaj<{ kategorie: Kategoria[] }>('/dictionary/device-categories'),

  urzadzenia: (
    opcje: {
      q?: string;
      customerId?: string;
      status?: StatusUrzadzenia;
      limit?: number;
      offset?: number;
    } = {},
  ) => zapytaj<ListaUrzadzen>(`/devices${parametry(opcje)}`),

  urzadzenie: (id: string) => zapytaj<Karta>(`/devices/${id}`),

  /** Wejście ze skanu naklejki: token albo cały adres z kodu QR. */
  urzadzeniePoQr: (token: string) => zapytaj<Karta>(`/devices/qr/${encodeURIComponent(token)}`),

  utworzUrzadzenie: (dane: NowaKarta) =>
    zapytaj<Karta>('/devices', { method: 'POST', body: JSON.stringify(dane) }),

  zmienUrzadzenie: (id: string, dane: ZmianaKarty) =>
    zapytaj<Karta>(`/devices/${id}`, { method: 'PATCH', body: JSON.stringify(dane) }),

  kontrahenci: (opcje: { q?: string; limit?: number; offset?: number } = {}) =>
    zapytaj<{ kontrahenci: Kontrahent[]; ile: number }>(`/customers${parametry(opcje)}`),

  slownikiSerwisu: () => zapytaj<SlownikiSerwisu>('/dictionary/serwis'),

  serwisanci: () => zapytaj<{ serwisanci: Osoba[] }>('/serwisanci'),

  zlecenia: (
    opcje: {
      q?: string;
      status?: StatusZlecenia;
      technicianId?: string;
      deviceId?: string;
      fakt?: 'bez-przypisania' | 'zaplanowane' | 'do-rozliczenia';
      limit?: number;
      offset?: number;
    } = {},
  ) => zapytaj<ListaZlecen>(`/tickets${parametry(opcje)}`),

  zlecenie: (id: string) => zapytaj<KartaZlecenia>(`/tickets/${id}`),

  utworzZlecenie: (dane: NoweZlecenie) =>
    zapytaj<KartaZlecenia>('/tickets', { method: 'POST', body: JSON.stringify(dane) }),

  zmienZlecenie: (id: string, dane: Partial<NoweZlecenie>) =>
    zapytaj<KartaZlecenia>(`/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(dane) }),

  /** Czynność z maszyny stanów — jedyny sposób na zmianę statusu. */
  czynnoscZlecenia: (id: string, akcja: string, powod?: string, clientOperationId?: string) =>
    zapytaj<KartaZlecenia>(`/tickets/${id}/czynnosci/${akcja}`, {
      method: 'POST',
      body: JSON.stringify({ powod, clientOperationId }),
    }),

  przypiszZlecenie: (
    id: string,
    dane: { technicianId?: string | null; termin?: string | null; clientOperationId?: string },
  ) =>
    zapytaj<KartaZlecenia>(`/tickets/${id}/przypisanie`, {
      method: 'POST',
      body: JSON.stringify(dane),
    }),

  dodajWizyte: (
    id: string,
    dane: { technicianId: string; termin: string; uwagi?: string; clientOperationId?: string },
  ) => zapytaj<KartaZlecenia>(`/tickets/${id}/wizyty`, { method: 'POST', body: JSON.stringify(dane) }),

  usunWizyte: (id: string, visitId: string) =>
    zapytaj<KartaZlecenia>(`/tickets/${id}/wizyty/${visitId}`, { method: 'DELETE' }),

  utworzProtokol: (id: string, dane: NowyProtokol) =>
    zapytaj<KartaZlecenia>(`/tickets/${id}/protokoly`, {
      method: 'POST',
      body: JSON.stringify(dane),
    }),

  montaze: (
    opcje: { q?: string; status?: StatusMontazu; limit?: number; offset?: number } = {},
  ) => zapytaj<ListaMontazy>(`/assembly-orders${parametry(opcje)}`),

  montaz: (id: string) => zapytaj<KartaMontazu>(`/assembly-orders/${id}`),

  utworzMontaz: (dane: NowyMontaz) =>
    zapytaj<KartaMontazu>('/assembly-orders', { method: 'POST', body: JSON.stringify(dane) }),

  zmienMontaz: (id: string, dane: ZmianaMontazu) =>
    zapytaj<KartaMontazu>(`/assembly-orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dane),
    }),

  podpiszMontaz: (id: string, rodzaj: RodzajPodpisu, clientOperationId?: string) =>
    zapytaj<KartaMontazu>(`/assembly-orders/${id}/podpisy/${rodzaj}`, {
      method: 'POST',
      body: JSON.stringify({ clientOperationId }),
    }),

  cofnijPodpisMontazu: (id: string, rodzaj: RodzajPodpisu) =>
    zapytaj<KartaMontazu>(`/assembly-orders/${id}/podpisy/${rodzaj}`, { method: 'DELETE' }),

  zakonczMontaz: (id: string, dane: ZakonczenieMontazu) =>
    zapytaj<KartaMontazu>(`/assembly-orders/${id}/zakoncz`, {
      method: 'POST',
      body: JSON.stringify(dane),
    }),

  utworzKontrahenta: (dane: NowyKontrahent) =>
    zapytaj<Kontrahent>('/customers', { method: 'POST', body: JSON.stringify(dane) }),
};

function parametry(opcje: Record<string, string | number | undefined>): string {
  const szukane = new URLSearchParams();
  for (const [klucz, wartosc] of Object.entries(opcje)) {
    if (wartosc !== undefined && wartosc !== '') szukane.set(klucz, String(wartosc));
  }
  const tekst = szukane.toString();
  return tekst ? `?${tekst}` : '';
}
